import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import "./style.css";
import {
  isAdminFromPayload,
  isCaptainFromPayload,
} from "../../../auth/adminEmails";

// Environment flag - set to true when in production, false for development/testing
const IS_PRODUCTION = true;

// Cognito configuration - set these after running the Cognito setup scripts
const COGNITO_CONFIG = {
  userPoolId: "us-east-1_fc1omeTGh",
  clientId: "54fiucjkk95o6gna3l5l3dnjuo",
  region: "us-east-1",
};

// Make config available globally for other components
window.COGNITO_CONFIG = COGNITO_CONFIG;

// Check if Cognito is configured
const isCognitoConfigured =
  COGNITO_CONFIG.userPoolId && COGNITO_CONFIG.clientId;

// Create the Cognito User Pool object
const userPool = isCognitoConfigured
  ? new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.userPoolId,
      ClientId: COGNITO_CONFIG.clientId,
    })
  : null;

// Function to format date to "Sat Apr 4" format
const formatEventDate = (dateString) => {
  if (!dateString) return "";

  // Parse the date and ensure it's treated as UTC
  const parts = dateString.split("-");
  if (parts.length < 3) return "";

  // Create date using year, month (0-indexed), day
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS Date
  const day = parseInt(parts[2], 10);

  // Create date object with the exact date (no time component)
  const date = new Date(Date.UTC(year, month, day));

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC", // Ensure no timezone adjustment
  });
};

export default function Login() {
  const navigate = useNavigate();
  // State management
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cognitoPassword, setCognitoPassword] = useState("");
  const [cognitoEmail, setCognitoEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [cognitoToken, setCognitoToken] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // New state for signup form
  const [showSignup, setShowSignup] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [signupErrorMessage, setSignupErrorMessage] = useState("");
  const [signupSuccessMessage, setSignupSuccessMessage] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [needVerification, setNeedVerification] = useState(false);

  // New state for social events
  const [activeTab, setActiveTab] = useState("events");
  const [socialEvents, setSocialEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [showSubscribePrompt, setShowSubscribePrompt] = useState(false);

  // New state for user info modal
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [currentFeatureType, setCurrentFeatureType] = useState(null);
  const [currentEventId, setCurrentEventId] = useState(null);

  // API URL for fetching events
  const API_URL =
    "https://ajje4ng1me.execute-api.us-east-1.amazonaws.com/prod/pending-join-requests";

  // Check for stored session on initial load
  useEffect(() => {
    // Check for stored token
    const storedToken = sessionStorage.getItem("cognitoIdToken");
    if (storedToken) {
      setCognitoToken(storedToken);
      setIsLoggedIn(true);

      // Parse token to get user info
      try {
        const payload = JSON.parse(atob(storedToken.split(".")[1]));

        // Check if we have stored profile info
        const storedProfileInfo = sessionStorage.getItem("userProfileInfo");
        if (storedProfileInfo) {
          const profileInfo = JSON.parse(storedProfileInfo);
          setUserInfo({
            email: payload.email,
            sub: payload.sub,
            groups: payload["cognito:groups"] || [],
            phone: profileInfo.phone || "",
            sex: profileInfo.sex || "",
            shirtSize: profileInfo.shirtSize || "",
            interestedEvents: profileInfo.interestedEvents || "",
            referredBy: profileInfo.referredBy || "",
          });
        } else {
          setUserInfo({
            email: payload.email,
            sub: payload.sub,
            groups: payload["cognito:groups"] || [],
          });
        }

        const userRoleValue = isAdminFromPayload(payload)
          ? "admin"
          : isCaptainFromPayload(payload)
          ? "captain"
          : payload["cognito:groups"]?.includes("Members")
          ? "member"
          : "user";

        setUserRole(userRoleValue);

        // Check expiration before redirecting — an expired token here
        // means the user came back to /login with a stale session;
        // surface the error instead of bouncing them to /dashboard
        // where the same expired token would just trigger another
        // bounce.
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < currentTime) {
          handleLogout();
          setErrorMessage("Your session has expired. Please login again.");
          return;
        }

        // Single post-login destination. Dashboard picks admin vs user
        // UI from the JWT — no need to branch on role here.
        navigate("/dashboard");
        return;
      } catch (e) {
        console.error("Error parsing JWT token:", e);
      }
    }
  }, [navigate]);

  // Load data when logged in
  useEffect(() => {
    if (isLoggedIn && activeTab === "events") {
      loadSocialEvents();
    }
  }, [isLoggedIn, cognitoToken, activeTab]);

  // Direct Cognito login (no redirect)
  const handleDirectCognitoLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!cognitoEmail || !cognitoPassword) {
      setErrorMessage("Email and password are required");
      setLoading(false);
      return;
    }

    const authenticationDetails = new AuthenticationDetails({
      Username: cognitoEmail,
      Password: cognitoPassword,
    });

    const cognitoUser = new CognitoUser({
      Username: cognitoEmail,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        // Get the ID + refresh tokens. We capture the refresh token
        // too so AuthContext can run its silent refresh loop after
        // login (previously the refresh token was only stored when
        // login went through AuthContext.login, leaving the
        // Login.js path unable to refresh).
        const idToken = result.getIdToken().getJwtToken();
        const refreshTokenValue = result.getRefreshToken().getToken();

        // Store tokens in sessionStorage (the source of truth that
        // authFetch + the apollo client read on every request).
        sessionStorage.setItem("cognitoIdToken", idToken);
        sessionStorage.setItem("cognitoRefreshToken", refreshTokenValue);
        setCognitoToken(idToken);

        // Parse user info from token (used both for local state and
        // for the AuthContext bootstrap below).
        let userInfoData = null;
        let role = "user";
        try {
          const payload = JSON.parse(atob(idToken.split(".")[1]));
          userInfoData = {
            email: payload.email,
            sub: payload.sub,
            groups: payload["cognito:groups"] || [],
          };
          setUserInfo(userInfoData);
          role = isAdminFromPayload(payload)
            ? "admin"
            : isCaptainFromPayload(payload)
            ? "captain"
            : payload["cognito:groups"]?.includes("Members")
            ? "member"
            : "user";
          setUserRole(role);
        } catch (e) {
          console.error("Error parsing JWT token:", e);
        }

        // Pre-populate localStorage with the same keys AuthContext
        // reads via useLocalStorage. Without this, when navigate
        // mounts AdminLogin (wrapped in AuthProvider), AuthProvider's
        // initial useState reads empty localStorage → isAuthenticated
        // is false on first render → AdminLogin returns null →
        // user sees a blank flicker that they often interpret as "the
        // page failed to load" and reload (which lands them at /login
        // again). The hydration effect inside AuthContext catches up
        // a render later, but by then the user has already navigated
        // away. Writing the same JSON shape useLocalStorage produces
        // makes the first render correct.
        try {
          if (typeof window !== "undefined" && window.localStorage) {
            localStorage.setItem("auth_token", JSON.stringify(idToken));
            localStorage.setItem(
              "refresh_token",
              JSON.stringify(refreshTokenValue)
            );
            if (userInfoData) {
              localStorage.setItem(
                "user_info",
                JSON.stringify(userInfoData)
              );
            }
            localStorage.setItem("user_role", JSON.stringify(role));
          }
        } catch (e) {
          // localStorage can be unavailable in private browsing on
          // some Safari versions — don't fail login over it; the
          // hydration effect still recovers (with the brief flicker).
          console.warn("Could not pre-populate localStorage:", e);
        }

        // Tell App.js and NavbarCustom that auth state changed in this
        // tab — sessionStorage writes don't fire the `storage` event in
        // the same tab they were written from.
        window.dispatchEvent(new Event("auth-changed"));

        // Single post-login destination. Dashboard.js renders the
        // admin UI for admins/captains and the user dashboard for
        // everyone else.
        setLoading(false);
        setCognitoPassword(""); // clear password from state
        navigate("/dashboard");
      },
      onFailure: (err) => {
        console.error("Authentication error:", err);
        setErrorMessage(err.message || "Authentication failed");
        setLoading(false);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Handle new password required (first login)
        setErrorMessage(
          "You need to change your password. Please contact an administrator."
        );
        setLoading(false);
      },
    });
  };

  // Logout
  const handleLogout = () => {
    // Clear token
    sessionStorage.removeItem("cognitoIdToken");
    sessionStorage.removeItem("userProfileInfo");
    setCognitoToken("");

    // With direct integration, we don't need to redirect for logout
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }

    setIsLoggedIn(false);
    setSocialEvents([]);
    setUserInfo(null);
    setUserRole(null);
  };

  // Load social events from API
  const loadSocialEvents = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      let headers = { "Content-Type": "application/json" };

      if (cognitoToken) {
        headers["Authorization"] = `Bearer ${cognitoToken}`;
      } else {
        setIsLoggedIn(false);
        return;
      }

      const response = await fetch(`${API_URL}?type=socialEvents&days=14`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setIsLoggedIn(false);
          throw new Error(
            "Session expired or unauthorized, please login again"
          );
        }
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();

      // Add formatted date for display before setting state
      const eventsWithFormattedDates = (data.upcomingEvents || []).map(
        (event) => {
          const eventCopy = { ...event };
          const dateParts = event.socialEventId.split("-");
          if (dateParts.length >= 3) {
            const fullDate = `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`;
            eventCopy.shortDate = formatEventDate(fullDate);
          }
          return eventCopy;
        }
      );

      setSocialEvents(eventsWithFormattedDates);
    } catch (error) {
      setErrorMessage("Failed to load social events: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle user info update
  const handleUserInfoUpdate = (updatedInfo) => {
    // Update the user info state
    setUserInfo((prev) => ({
      ...prev,
      ...updatedInfo,
    }));

    // Save updated information to session storage to persist it
    try {
      const storedToken = sessionStorage.getItem("cognitoIdToken");
      if (storedToken) {
        // Parse the token to get user info
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        // Store the additional user info in session storage
        sessionStorage.setItem(
          "userProfileInfo",
          JSON.stringify({
            ...payload,
            ...updatedInfo,
          })
        );
      }
    } catch (e) {
      console.error("Error saving user profile info to session storage:", e);
    }
  };

  // Handle signup form submission
  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupErrorMessage("");
    setSignupSuccessMessage("");

    // Basic validation
    if (!signupEmail || !signupPassword) {
      setSignupErrorMessage("Email and password are required");
      return;
    }

    if (signupPassword !== signupConfirmPassword) {
      setSignupErrorMessage("Passwords do not match");
      return;
    }

    setLoading(true);

    const attributeList = [
      new CognitoUserAttribute({
        Name: "email",
        Value: signupEmail,
      }),
    ];

    userPool.signUp(
      signupEmail,
      signupPassword,
      attributeList,
      null,
      (err, result) => {
        setLoading(false);

        if (err) {
          setSignupErrorMessage(err.message || "Registration failed");
          return;
        }

        setSignupSuccessMessage("");
        setNeedVerification(true);
      }
    );
  };

  // Handle verification code submission
  const handleVerifyCode = (e) => {
    e.preventDefault();
    setSignupErrorMessage("");
    setSignupSuccessMessage("");

    if (!verificationCode) {
      setSignupErrorMessage("Verification code is required");
      return;
    }

    setLoading(true);

    const cognitoUser = new CognitoUser({
      Username: signupEmail,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
      setLoading(false);

      if (err) {
        setSignupErrorMessage(err.message || "Verification failed");
        return;
      }

      setSignupSuccessMessage(
        "Verification successful! You can now sign in with your credentials."
      );
      setNeedVerification(false);
      setShowSignup(false);
      setCognitoEmail(signupEmail);
    });
  };

  // Toggle between login and signup forms.
  // The "Sign up" link on the login page now navigates to a real
  // /signup page (Cognito signup + verification + socialUsers link).
  // The legacy /join form stays available at /join for people who
  // want the longer onboarding flow but don't need a login.
  const toggleSignup = () => {
    if (IS_PRODUCTION) {
      window.location.href = "/signup";
      return;
    }
    // In development/testing, show the inline signup form (still works
    // — same Cognito API — but lives on the login page).
    setShowSignup(!showSignup);
    setSignupErrorMessage("");
    setSignupSuccessMessage("");
    setErrorMessage("");
    setNeedVerification(false);
  };

  // Handle toggling event card expansion
  const toggleEventExpansion = (eventId) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  // Generate random attendance number (8-14)
  const getRandomAttendance = () => {
    return Math.floor(Math.random() * 7) + 8; // Random number between 8-14
  };

  // Handle showing the appropriate modal based on user information
  const handleShowAttendees = (e, eventId) => {
    e.stopPropagation(); // Prevent collapsing the card when button is clicked

    // If user is already a member or admin, they can see everything
    if (userRole === "member" || userRole === "admin") {
      // Future: implement showing attendee list directly
      console.log("Show attendees list for event:", eventId);
      return;
    }

    // Check if we have phone and sex info in userInfo
    if (!userInfo.phone || !userInfo.sex) {
      setCurrentEventId(eventId);
      setCurrentFeatureType("attendees");
      setShowUserInfoModal(true);
    } else if (userInfo.sex === "female") {
      // Future: implement showing attendee list for females
      console.log("Show attendees list for female user, event:", eventId);
    } else {
      // For males, show subscription prompt
      setShowSubscribePrompt(true);
    }
  };

  // Handle tab change with checking for user info
  const handleTabChange = (tab) => {
    if (tab === "chats" && !(userRole === "member" || userRole === "admin")) {
      // Check if we have phone and sex info in userInfo
      if (!userInfo.phone || !userInfo.sex) {
        setCurrentFeatureType("chats");
        setShowUserInfoModal(true);
      } else if (userInfo.sex === "female") {
        // Navigate directly to chats for females
        navigateToChats();
      } else {
        // For males, show subscription prompt
        setShowSubscribePrompt(true);
      }
    } else {
      setActiveTab(tab);
    }
  };

  // Handle user info submission
  const handleUserInfoSubmit = async (userData) => {
    // Update the user info
    setUserInfo((prev) => ({
      ...prev,
      phone: userData.phone,
      sex: userData.sex,
      shirtSize: userData.shirtSize,
      interestedEvents: userData.interestedEvents,
      referredBy: userData.referredBy,
    }));

    // Save updated information to session storage to persist it
    try {
      const storedToken = sessionStorage.getItem("cognitoIdToken");
      if (storedToken) {
        // Parse the token to get user info
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        // Store the additional user info in session storage
        sessionStorage.setItem(
          "userProfileInfo",
          JSON.stringify({
            ...payload,
            phone: userData.phone,
            sex: userData.sex,
            shirtSize: userData.shirtSize,
            interestedEvents: userData.interestedEvents,
            referredBy: userData.referredBy,
          })
        );
      }
    } catch (e) {
      console.error("Error saving user profile info to session storage:", e);
    }

    // Close the modal
    setShowUserInfoModal(false);

    // Handle the requested feature based on sex
    if (userData.sex === "female") {
      if (currentFeatureType === "chats") {
        navigateToChats();
      } else if (currentFeatureType === "attendees") {
        // Future: implement showing attendee list for females
        console.log(
          "Show attendees list for female user, event:",
          currentEventId
        );
      }
    } else {
      // For males, show subscription prompt
      setShowSubscribePrompt(true);
    }
  };

  // Close subscription prompt
  const closeSubscribePrompt = () => {
    setShowSubscribePrompt(false);
  };

  // Handle subscription button click
  const handleSubscribe = () => {
    // Navigate to subscription page
    window.location.href = "/subscribe";
  };

  // Navigate to chats page
  const navigateToChats = () => {
    window.location.href = "/chats";
  };

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="login-container">
        <div
          style={{
            textAlign: "center",
            padding: "16px 18px",
            margin: "0 auto 18px",
            maxWidth: "420px",
            background: "#fff3cd",
            border: "1px solid #ffeeba",
            borderRadius: "6px",
            color: "#856404",
          }}
        >
          <div
            style={{
              fontSize: "22px",
              fontWeight: 700,
              letterSpacing: "0.5px",
            }}
          >
            FOR ADMIN USE ONLY
          </div>
          <div style={{ fontSize: "13px", marginTop: "4px" }}>
            This site is currently in testing for regular users.
          </div>
        </div>
        <div className="login-box">
          <h1>{showSignup ? "Sign Up" : "Sign In"}</h1>

          {!showSignup && !needVerification ? (
            <>
              <div className="cognito-login">
                <form onSubmit={handleDirectCognitoLogin}>
                  <div className="form-group">
                    <label htmlFor="cognitoEmail">Email</label>
                    <input
                      type="email"
                      id="cognitoEmail"
                      value={cognitoEmail}
                      onChange={(e) => setCognitoEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="cognitoPassword">Password</label>
                    <input
                      type="password"
                      id="cognitoPassword"
                      value={cognitoPassword}
                      onChange={(e) => setCognitoPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                    />
                  </div>
                  {errorMessage && (
                    <div className="error-message">{errorMessage}</div>
                  )}
                  <button
                    type="submit"
                    className="login-btn"
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign in"}
                  </button>
                </form>
                <div className="auth-links">
                  <button onClick={toggleSignup} className="switch-auth-btn">
                    Don't have an account? Sign up
                  </button>
                </div>
              </div>
            </>
          ) : showSignup && !needVerification ? (
            <>
              <div className="cognito-login">
                {signupSuccessMessage && (
                  <div className="success-message">{signupSuccessMessage}</div>
                )}
                {signupErrorMessage && (
                  <div className="error-message">{signupErrorMessage}</div>
                )}
                <form onSubmit={handleSignup}>
                  <div className="form-group">
                    <label htmlFor="signupEmail">Email</label>
                    <input
                      type="email"
                      id="signupEmail"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="signupPassword">Password</label>
                    <input
                      type="password"
                      id="signupPassword"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      placeholder="Create password"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={signupConfirmPassword}
                      onChange={(e) => setSignupConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      required
                    />
                  </div>
                  <div className="password-requirements">
                    <p>Password requirements:</p>
                    <ul>
                      <li>At least 8 characters long</li>
                      <li>Contains uppercase letters</li>
                      <li>Contains lowercase letters</li>
                      <li>Contains numbers</li>
                      <li>Contains special characters</li>
                    </ul>
                  </div>
                  <button
                    type="submit"
                    className="login-btn"
                    disabled={loading}
                  >
                    {loading ? "Creating Account..." : "Create Account"}
                  </button>
                </form>
                <div className="auth-links">
                  <button onClick={toggleSignup} className="switch-auth-btn">
                    Already have an account? Sign in
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="verification-form">
              {signupSuccessMessage && (
                <div className="success-message">{signupSuccessMessage}</div>
              )}
              {signupErrorMessage && (
                <div className="error-message">{signupErrorMessage}</div>
              )}
              <p>
                Please check your email for a verification code and enter it
                below.
              </p>
              <form onSubmit={handleVerifyCode}>
                <div className="form-group">
                  <label htmlFor="verificationCode">Verification Code</label>
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter verification code"
                    required
                  />
                </div>
                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? "Verifying..." : "Verify Code"}
                </button>
              </form>
              <button
                onClick={() => {
                  setNeedVerification(false);
                  setShowSignup(false);
                }}
                className="switch-auth-btn"
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback for the brief moment between a successful login and the
  // navigate("/dashboard") taking effect. Dashboard.js is the single
  // post-login destination — we no longer render UserDashboard inline
  // here.
  return null;
}
